import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useCreateScheduleEntry } from './useCreateScheduleEntry';
import { log as logger } from '../lib/logger';
import { useAuthStore } from '../stores/authStore';
import { localWallClockToInstant } from '../components/schedule/scheduleHelpers';

async function logActivity(businessId: string, type: string, desc: string, entityType: string, entityId: string, userId: string) {
  try { await supabase.from('activity_log').insert({ business_id: businessId, activity_type: type, description: desc, entity_type: entityType, entity_id: entityId, logged_by: userId }); } catch { /* non-critical */ }
}

interface CreateTimeOffData {
  business_id: string;
  user_id: string;
  date: string;
  end_date?: string;
  type: string;
  reason?: string;
  approved_by: string;
}

export function useCreateTimeOff() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createScheduleEntry = useCreateScheduleEntry();

  const createTimeOff = useCallback(async (data: CreateTimeOffData): Promise<boolean> => {
    setLoading(true); setError(null);
    try {
      if (!data.user_id) { setError('Team member is required'); setLoading(false); return false; }
      if (!data.date) { setError('Start date is required'); setLoading(false); return false; }

      // 1. Create leave request (HR record).
      const { data: lr, error: lrErr } = await supabase.from('leave_requests').insert({
        business_id: data.business_id,
        user_id: data.user_id,
        date: data.date,
        end_date: data.end_date || null,
        type: data.type || 'personal',
        reason: data.reason || null,
        status: 'approved',
        approved_by: data.approved_by,
        approved_at: new Date().toISOString(),
      }).select('id').maybeSingle();
      if (lrErr) throw lrErr;

      await logActivity(data.business_id, 'created', `Time off approved: ${data.type} starting ${data.date}`, 'leave_request', lr?.id ?? '', data.approved_by);

      // 2. Schedule projection via canonical RPC. type='block' per WIRING semantics
      // (time-off is blocked time, not a personal calendar event).
      const endDate = data.end_date || data.date;
      // start_at/end_at are timestamptz — build true instants at the business tz
      // (A2 P2), not naive wall-clock-as-UTC strings.
      const tz = useAuthStore.getState().business?.timezone ?? null;
      const entryRes = await createScheduleEntry({
        type: 'block',
        title: `Time off — ${data.type}`,
        startAt: localWallClockToInstant(data.date, '00:00:00', tz),
        endAt: localWallClockToInstant(endDate, '23:59:59', tz),
        assignedTo: [data.user_id],
        notes: data.reason || undefined,
        idempotencyKey: crypto.randomUUID(),
      });
      if (!entryRes.success) {
        logger.warn('Schedule entry creation failed for time-off:', lr?.id ?? '(no leave request id)', entryRes.error.code);
      }

      setLoading(false); return true;
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to create time off'); setLoading(false); return false; }
  }, [createScheduleEntry]);

  return { createTimeOff, loading, error };
}
